import { PortalModule } from '@angular/cdk/portal';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TOOLTIP_MESSAGES } from '@app/constants';
import { ComponentCanDeactivate } from '@app/core/guards';
import { DialogService, FeedbackService, FlyoutService, NavigationService } from '@app/core/services';
import { DocMgmtContentStateEnum, DocumentType, UrlParamStateEnum, UrlParamsEnum } from '@app/enums';
import { DocMgmtFile, ExtendedFile, FileStatus } from '@app/interfaces';
import { BaseFlyoutComponent } from '@app/shared/components/base-flyout.component';
import { KebabAction , KebabComponent } from '@app/shared/components/kebab/kebab.component';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { getIconByFilePath, isEmbeddableFileType, isImageFileType, sortAlphabetically, sortByDate, toShortDate } from '@app/utils';
import { DocManagementService } from './services/doc-management.service';
import { NgForKeyDirective, NgForTrackByItemDirective } from '../../directives/ng-for-track-by-id.directive';
import { RequiredNoWhitespaceDirective } from '../../directives/validators/required-no-whitespace.directive';
import { FileUploadDnDComponent } from '../file-upload-dnd/file-upload-dnd.component';

enum SortOptions {
	ABC = 'Alphabetically',
	DATE = 'Most Recent',
	DATE_REVERSE = 'Oldest',
}

const documentIsInNonFunctionalState = (contentState: DocMgmtContentStateEnum): boolean =>
	[DocMgmtContentStateEnum.Pending, DocMgmtContentStateEnum.RequiresVirusScan].indexOf(contentState) > -1;

@Component({
    selector: 'sl-doc-management',
    templateUrl: './doc-management.component.html',
    styleUrls: ['./doc-management.component.less'],
    standalone: true,
    imports: [
        NgIf,
        MatButtonModule,
        MatIconModule,
        PortalModule,
        MatTabsModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        NgFor,
        NgForTrackByItemDirective,
        MatOptionModule,
        NgForKeyDirective,
        NgClass,
        MatTooltipModule,
        MatCheckboxModule,
        KebabComponent,
        ReactiveFormsModule,
        FileUploadDnDComponent,
        MatProgressBarModule,
        MatInputModule,
        RequiredNoWhitespaceDirective,
    ],
})
export class DocManagementComponent extends BaseFlyoutComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {
	@Input() documentType: DocumentType;
	@Input() set entityId(value: number) {
		if (value && value !== this.internalEntityId) {
			this.internalEntityId = value;

			if (this.active) {
				this.loadData();
			}
		}
	}
	get entityId(): number {
		return this.internalEntityId;
	}
	@Input() permissionLevel: null | 'readOnly' | 'viewEdit' = null;
	@Input() hasConfidentialAccess = false;
	@Input() hasDeleteAccess = false;
	@Input() hideButtonText = true;
	@Input() decorateRoute = true;
	@Input() sendAttachments = false;
	@Input() sendAttachmentWithEmail = false;
	/**
	 * Tooltip text to show over the Manage Documents Button when a user hovers over it.
	 */
	@Input() buttonTitle: string;
	@Input() hideDocumentButton = false;
	@Input() flyoutHeaderTitle: string;
	@Output() docMgmtEditedEmitter = new EventEmitter<string>();

	@ViewChild('docMgmtForm') docMgmtForm: NgForm;
	@ViewChild('docMgmtFormEdit') docMgmtFormEdit: NgForm;

	get sort(): SortOptions {
		return this.internalSort;
	}
	set sort(value: SortOptions) {
		this.internalSort = value;
		this.sortData(this.storedFiles);
	}
	canUseFeature = false;
	isReadOnly = false;
	isAccessible = false;
	sortOptions = Object.values(SortOptions);
	storedFiles: DocMgmtFile[] = [];
	files: { [id: string]: ExtendedFile } = {};
	editing: DocMgmtFile | undefined;
	deleting: string[] = [];
	active = false; // flag for toggling UI and API calls
	tabSelectedIndex = 0; // Required for showing/hiding flyout sticky footer
	metaDataForm: UntypedFormGroup;
	filesToEdit: UntypedFormArray;
	actions: KebabAction<string>[] = [
		{
			label: 'View',
			onClick: (row: string): void => {
				const url = new URL(window.location.toString());
				const path = `${url.pathname}${url.search}`;
				this.flyoutService.close();
				this.router.navigate([`/document/view/${row}`], { queryParams: { ref: encodeURIComponent(path) } });
			},
			hidden: (row: string): boolean => {
				const file = this.storedFiles.find(({ id }) => id === row);
				return !isEmbeddableFileType(file?.name) && !isImageFileType(file?.name);
			},
			disabled: (row: string): boolean => {
				const file = this.storedFiles.find(({ id }) => id === row);
				return documentIsInNonFunctionalState(file.contentState);
			},
			disabledMessage: (row: string): string => {
				const file = this.storedFiles.find(({ id }) => id === row);
				return documentIsInNonFunctionalState(file.contentState) ? TOOLTIP_MESSAGES.documentBeingProcessed : '';
			},
		},
		{
			label: 'Download',
			onClick: (row: string): void => {
				this.downloadFile(this.storedFiles.find(({ id }) => id === row));
			},
			disabled: (row: string): boolean => {
				const file = this.storedFiles.find(({ id }) => id === row);
				return documentIsInNonFunctionalState(file.contentState);
			},
			disabledMessage: (row: string): string => {
				const file = this.storedFiles.find(({ id }) => id === row);
				return documentIsInNonFunctionalState(file.contentState) ? TOOLTIP_MESSAGES.documentBeingProcessed : '';
			},
		},
		{
			label: 'Edit',
			onClick: (row: string): void => {
				const file = this.storedFiles.find(({ id }) => id === row);
				this.editing = {
					...file,
					metadata: { ...file.metadata },
				};
			},
			hidden: (): boolean => !this.isAccessible,
		},
		{
			label: 'Delete',
			onClick: (row: string): void => {
				this.deleteStoredFile(row);
			},
			hidden: (): boolean => !this.hasDeleteAccess,
		},
	];

	private internalEntityId: number;
	private internalSort: SortOptions;
	private readonly destroy$ = new Subject();

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly formBuilder: UntypedFormBuilder,
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly feedbackService: FeedbackService,
		protected readonly navigationService: NavigationService,
		protected readonly flyoutService: FlyoutService,
		private readonly docManagementService: DocManagementService
	) {
		super(flyoutService, dialogService);

		this.metaDataForm = this.formBuilder.group({
			files: this.formBuilder.array([]),
		});

		this.filesToEdit = this.metaDataForm.get('files') as UntypedFormArray;
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.docMgmtForm?.dirty && !this.docMgmtFormEdit?.dirty && Object.values(this.files).length === 0;

	/**
	 * The submit button is disabled when a user has an invalid
	 * form or when _any_ of the files are being processed (uploading)
	 * or being deleted.
	 */
	formDisabled(): boolean {
		// If the user is editing a single document, check editing form status
		if (this.editing) {
			return !!this.docMgmtFormEdit && this.docMgmtFormEdit.invalid;
		}

		// If any of the files are being processed (uploading) or deleted, don't allow user to save
		if (this.files && Object.values(this.files).length) {
			const filesUploadingOrDeleting = Object.values(this.files).some((file) => file.status !== FileStatus.EDITING);

			// Only stop the form check when status is not editing
			if (filesUploadingOrDeleting) {
				return true;
			}
		}

		return !this.filesToEdit.controls.length || (!!this.docMgmtForm && this.docMgmtForm.invalid);
	}

	activeUrlParam(): boolean {
		return !!new URLSearchParams(window.location.search).get(UrlParamsEnum.DOC_MANAGEMENT);
	}

	ngOnInit(): void {
		// Page was refreshed with an active url param
		if (this.activeUrlParam() && this.canUseFeature) {
			// Without setTimeout the arg `portal` in is undefined - navigationService.attachPortal(portal)
			// Tried using different lifecycle hooks to no avail
			setTimeout(() => {
				this.activate();
			}, 200);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.permissionLevel?.currentValue) {
			this.canUseFeature = !!this.permissionLevel;
			this.isReadOnly = this.permissionLevel === 'readOnly';
			// -1 is a Global Admin
			this.isAccessible = ['viewEdit', '-1'].indexOf(this.permissionLevel) > -1;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	displayDate(date: string): string {
		return `${toShortDate(date)} @ ${moment(date).format('h:mm A')}`;
	}

	setCurrentTab(index: number): void {
		this.tabSelectedIndex = index;
	}

	onTabChange(event: MatTabChangeEvent): void {
		this.tabSelectedIndex = event.index;
	}

	/**
	 * Load the index GET to list all the files associated with the Vault folder.
	 * The Vault folder is built using the documentType and entity id.
	 * For example: PurchaseOrders/123
	 */
	loadData(): void {
		if (this.entityId) {
			this.docManagementService.listByEntity(this.documentType, this.entityId).subscribe((data) => {
				// Add the user field displayName
				const temp = data.items
					.map((item) => {
						item.displayName = this.displayName(item);
						return item;
					})
					.filter((item) => !item.metadata?.IsConfidential || this.hasConfidentialAccess);

				this.sortData(temp);
			});
		}
	}

	sortData(list: DocMgmtFile[]): void {
		switch (this.sort) {
			case SortOptions.DATE:
				this.storedFiles = sortByDate(list, 'updatedAt', true);
				break;
			case SortOptions.DATE_REVERSE:
				this.storedFiles = sortByDate(list, 'updatedAt');
				break;
			case SortOptions.ABC:
			default:
				this.storedFiles = sortAlphabetically(list, 'displayName');
				break;
		}
	}

	activate(): void {
		// Without setTimeout, the flyout does not open. ¯\_(ツ)_/¯
		setTimeout(() => {
			this.toggleFlyout();
		});

		this.active = true;
		this.loadData();

		// Update the url to contain a get param to retain UI state
		if (this.decorateRoute && !this.activeUrlParam()) {
			this.router.navigate([], {
				relativeTo: this.route,
				queryParamsHandling: 'merge',
				queryParams: {
					[UrlParamsEnum.DOC_MANAGEMENT]: UrlParamStateEnum.OPEN,
				},
				replaceUrl: true,
			});
		}
	}

	deactivate(): void {
		const callback = () => {
			this.toggleFlyout();
			this.active = false;

			// Update the url to contain a get param to retain UI state
			if (this.decorateRoute) {
				this.router.navigate([], {
					relativeTo: this.route,
					replaceUrl: true,
				});
			}
		};

		if (this.files && Object.keys(this.files).length) {
			this.cancelUploading(callback);
		} else if (this.editing && this.docMgmtFormEdit?.dirty) {
			this.cancelEditing(callback);
		} else {
			callback();
		}
	}

	/**
	 * Submit callback for editing an existing document.
	 */
	handleSubmitEditing(): void {
		// Trigger validation
		this.docMgmtFormEdit.form.markAllAsTouched();

		if (this.docMgmtFormEdit.valid) {
			this.docManagementService
				.updateMetaDataEntity(
					this.editing.id,
					this.editing.metadata.Title as string,
					this.editing.metadata.Description as string,
					this.editing.metadata.IsConfidential as boolean,
					this.editing.metadata.IsSendWithEmail as boolean
				)
				.subscribe(() => {
					this.docMgmtEditedEmitter.emit(this.editing.id);
					this.docMgmtFormEdit.resetForm();
					delete this.editing;
					this.loadData();
					this.feedbackService.success('updated', 'Document');
				});
		}
	}

	/**
	 * Submit callback for updating the entity with new documents
	 */
	handleSubmitUpdating(): void {
		const files = this.docMgmtForm.value.files.map((file) => ({
			...file,
			...this.files[file.id],
		}));
		const request = files.map((file, index) =>
			this.docManagementService.updateMetaDataEntity(file.id, file.Title, file.Description, file.IsConfidential, file.IsSendWithEmail).pipe(
				catchError(() => {
					this.filesToEdit.removeAt(index);
					delete this.files[file.id];
					return of([]);
				})
			)
		);

		// Combine multiple http request into a single observable
		forkJoin(request)
			.subscribe(() => {
				this.docMgmtEditedEmitter.emit(request?.file?.id);
				this.docMgmtForm.form.reset();
			})
			.add(() => {
				// Clear all the files to view DragNDrop
				this.filesToEdit.clear();
				this.files = {};

				// Change back to the first tab.
				this.tabSelectedIndex = 0;

				this.feedbackService.success('created', `Document${request.length > 1 ? 's' : ''}`, { isPlural: request.length > 1 });

				// Reload the data
				this.loadData();
			});
	}

	cancelEditing(confirmCallback: () => void = undefined): void {
		this.dialogService.canDeactivate(this, {
			onCanDeactivate: (): void => {
				this.docMgmtFormEdit.resetForm();
				delete this.editing;
				confirmCallback?.();
			},
		});
	}

	/**
	 * Cancel Uploading when a user is uploading new documents, not
	 * editing existing documents. The callback is used by the close
	 * action for the slider.
	 */
	cancelUploading(confirmCallback: () => void = undefined): void {
		if (this.files && Object.keys(this.files).length) {
			this.dialog
				.open(ModalComponent, {
					data: {
						title: 'Delete recently uploaded files?',
						message: 'Canceling before saving will delete the files.',
					},
				})
				.afterClosed()
				.subscribe((confirm) => {
					if (confirm) {
						Object.keys(this.files).forEach((id) => {
							this.removeFile(id, false);
						});
						this.tabSelectedIndex = 0;
						this.docMgmtForm.reset();

						if (confirmCallback) {
							confirmCallback();
						}
					}
				});
		} else {
			this.tabSelectedIndex = 0;
		}
	}

	/**
	 * Removing a file happens before the file
	 * is part of the storedFiles array. The file
	 * was just uploaded or is being uploaded.
	 */
	removeFile(id: string, requiresConfirmation = true): void {
		const file = this.files[id];
		const completeTask = (confirm: boolean) => {
			if (confirm) {
				// Cancel fetch request
				if (file.subscription) {
					file.subscription.unsubscribe();
				}

				this.docMgmtEditedEmitter.emit(id);

				// Trigger a delete from Vault if we have a Vault Id
				if (file.id !== file.name) {
					this.files[id].status = FileStatus.DELETING;
					this.docManagementService.deleteEntity(file.id).subscribe(() => {
						// Remove files after deleted
						const controlIndexToRemove = Object.keys(this.files).findIndex((removeId: string) => removeId === id);
						this.filesToEdit.removeAt(controlIndexToRemove);
						delete this.files[id];
					});
					return;
				}

				const index = Object.keys(this.files).findIndex((removeId: string) => removeId === id);
				this.filesToEdit.removeAt(index);
				delete this.files[id];
			}
		};

		if (!requiresConfirmation) {
			completeTask(true);
			return;
		}

		this.deleteFileConfirmation(completeTask);
	}

	downloadFile(file: DocMgmtFile): void {
		this.docManagementService.getEntity(file.id, 'attachment').subscribe((data) => {
			window.open(data.downloadUrl);
		});
	}

	/**
	 * Callback function for a file system selection or drag and drop.
	 * - Flip the file status to processing and add the file to the form array
	 * - Loop through each file to create a new empty entity in Vault
	 * - Upload the file directly to S3 with the one time use url from Vault
	 * - Flip the file status to editing to allow for metadata edits
	 */
	handleSelectedFiles(files: ExtendedFile[]): void {
		this.files = {
			...this.files,
			...files
				.filter((f) => f)
				.reduce((acc, file, index) => {
					// Add temp id before Vault entity creation to allow for deletion if file is rejected
					file.id = file.name;

					// Display progress bar
					file.status = FileStatus.PROCESSING;

					// Add a new item to the form array
					this.filesToEdit.insert(
						index,
						this.formBuilder.group({
							Title: file.name,
							Description: '',
							IsConfidential: false,
							IsSendWithEmail: this.sendAttachmentWithEmail,
							id: file.id,
						})
					);

					acc[file.id] = file;
					return acc;
				}, {}),
		};

		Object.entries(this.files).forEach(([id, file]) => {
			this.docManagementService.createEntity(this.documentType, this.entityId, file.name).subscribe({
				next: (data) => {
					// Track the id - required to delete and update with Vault
					this.files[id].id = data.id;

					// Upload the file to S3, track progress and map subscription to allow for canceling upload
					this.files[id].subscription = this.docManagementService.uploadToS3(data.upload.url, file).subscribe({
						// Successful upload or Upload Progress
						next: (event: HttpEvent<unknown>) => {
							if (event && event.type === HttpEventType.UploadProgress && this.files[id]) {
								// Update upload progress bar
								this.files[id].progress = Math.round((100 * event.loaded) / event.total);
							}
							if (event && event.type === HttpEventType.Response && this.files[id]) {
								// File upload is complete, now view meta data edit form
								this.files[id].status = FileStatus.EDITING;
							}
						},
						// S3 Upload Error - need to handle errors due to lack in interceptors used in s3 upload
						error: () => {
							this.feedbackService.error(`There was a problem uploading the file ${this.files[id].name}. Please try again.`, 'flyout');
							this.removeFile(id, false);
						},
					});
				},
				// Failed to create a Vault entity, not S3 upload.
				error: (error) => {
					const defaultError = () =>
						this.feedbackService.error(
							`There was a problem uploading the file ${this.files[id].name}. The file type might be accepted.`,
							'flyout'
						);

					if (error?.error?.ExceptionMessage) {
						try {
							const response = JSON.parse(error.error.ExceptionMessage);
							if (response?.message) {
								this.feedbackService.error(response?.message);
							}
						} catch (parseError) {
							defaultError();
						}
					} else {
						defaultError();
					}

					// Clean up partial upload on failed Vault entity creation
					this.removeFile(id, false);
				},
			});
		});
	}

	/**
	 * Default to file name is missing metadata Title
	 */
	displayName(file: DocMgmtFile): string {
		return (file?.metadata?.Title as string) ?? file.name;
	}

	hasTitle(file: DocMgmtFile): boolean {
		return !!file?.metadata?.Title;
	}

	/**
	 * Returns Material icon ligature
	 */
	fileIcon(filePath: string): string {
		return getIconByFilePath(filePath);
	}

	/**
	 * Deleting a stored file is used for files which
	 * are already stored in Vault, not recently uploaded.
	 */
	deleteStoredFile(row: string): void {
		this.deleteFileConfirmation((confirm) => {
			if (confirm) {
				this.deleting.push(row);
				this.docManagementService.deleteEntity(row).subscribe({
					next: () => {
						this.docMgmtEditedEmitter.emit(row);
						this.storedFiles = this.storedFiles.filter(({ id }) => id !== row);
						this.feedbackService.success('deleted', 'Document');
					},
					error: () => {
						this.deleting.splice(this.deleting.indexOf(row), 1);
					},
				});
			}
		});
	}

	private deleteFileConfirmation(callback: (confirm: boolean) => void) {
		this.dialog
			.open(ModalComponent, {
				data: {
					title: 'Delete file permanently?',
					message: 'Deleting the file can not be undone. Please confirm deletion.',
				},
			})
			.afterClosed()
			.subscribe(callback);
	}
}
